/**
 * Helper functions untuk bekerja dengan config
 */

/**
 * Mendapatkan nilai dari config dengan path nested
 * Contoh: getConfigValue(config, 'api.baseUrl') => config.api.baseUrl
 */
export const getConfigValue = (config, path, defaultValue = null) => {
  if (!config) return defaultValue;

  const keys = path.split(".");
  let value = config;

  for (const key of keys) {
    if (value && typeof value === "object" && key in value) {
      value = value[key];
    } else {
      return defaultValue;
    }
  }

  return value !== undefined ? value : defaultValue;
};

/**
 * Mendapatkan API URL dari config
 */
export const getApiUrl = () => {
  return process.env.API_URL || "";
};

/**
 * Mendapatkan image URL dari config
 */
export const getImageUrl = () => {
  return process.env.IMAGE_URL || "";
};

/**
 * Mendapatkan site name dari config
 */
export const getSiteName = (config) => {
  return getConfigValue(config, "site.name", "BaseStore");
};

/**
 * Mendapatkan site title dari config
 */
export const getSiteTitle = (config) => {
  return getConfigValue(config, "site.title", "BaseStore - Online Store");
};

/**
 * Mendapatkan site description dari config
 */
export const getSiteDescription = (config) => {
  return getConfigValue(
    config,
    "site.description",
    "Online store untuk berbagai kebutuhan Anda",
  );
};

/**
 * Mendapatkan Google Analytics ID dari config
 */
export const getGoogleAnalyticsId = (config) => {
  return getConfigValue(config, "analytics.googleAnalyticsId", null);
};

/**
 * Mendapatkan Facebook Pixel ID dari config
 */
export const getFacebookPixelId = (config) => {
  return getConfigValue(config, "analytics.facebookPixelId", null);
};

/**
 * Mendapatkan token verifikasi Google Search Console dari config
 */
export const getGoogleSiteVerification = (config) => {
  return getConfigValue(config, "analytics.googleSiteVerification", null);
};

export const getBingSiteVerification = (config) => {
  return getConfigValue(config, "analytics.bingSiteVerification", null);
};

export const getYandexSiteVerification = (config) => {
  return getConfigValue(config, "analytics.yandexSiteVerification", null);
};

/**
 * Mendapatkan theme colors dari config
 */
export const getThemeColors = (config) => {
  return getConfigValue(config, "theme.colors", {
    primary: "#3B82F6",
    secondary: "#8B5CF6",
  });
};

/**
 * Mendapatkan logo dari config
 */
export const getLogo = (config, type = "light") => {
  const logoPath = type === "light" ? "logo.light" : "logo.dark";
  return getConfigValue(config, logoPath, null);
};

/**
 * Build logo URL dari config (handle relative path dari API)
 */
export const buildLogoUrl = (logoPath, imageBaseUrl = "") => {
  if (!logoPath) {
    return null;
  }

  // Jika sudah full URL
  if (logoPath.startsWith("http://") || logoPath.startsWith("https://")) {
    return logoPath;
  }

  // Jika path relatif dari public folder
  if (logoPath.startsWith("/")) {
    return logoPath;
  }

  // Jika path relatif dari API
  if (imageBaseUrl) {
    const baseUrl = imageBaseUrl.replace(/\/$/, "");
    const imagePath = logoPath.replace(/^\//, "");
    return `${baseUrl}/${imagePath}`;
  }

  return logoPath;
};

/**
 * Mendapatkan favicon dari config
 */
export const getFavicon = (config) => {
  return getConfigValue(config, "logo.favicon", "/favicon.ico");
};

/**
 * Mendapatkan default title dari config
 */
export const getDefaultTitle = (config) => {
  return getConfigValue(config, "site.title", "BaseStore");
};

/**
 * Mendapatkan default description dari config
 */
export const getDefaultDescription = (config) => {
  return getConfigValue(
    config,
    "site.description",
    "Online store untuk berbagai kebutuhan Anda",
  );
};

/**
 * Mendapatkan default keywords dari config
 */
export const getDefaultKeywords = (config) => {
  return getConfigValue(
    config,
    "seo.defaultMeta.keywords",
    "toko online, belanja online, ecommerce",
  );
};

/**
 * Mendapatkan author dari config
 */
export const getAuthor = (config) => {
  return getConfigValue(config, "seo.defaultMeta.author", getSiteName(config));
};

/**
 * Mendapatkan robots dari config
 */
export const getRobots = (config) => {
  return getConfigValue(config, "seo.defaultMeta.robots", "index, follow");
};

/**
 * Mendapatkan OG image dari config
 */
export const getOgImage = (config) => {
  const ogImage = getConfigValue(
    config,
    "logo.og_image",
    "/images/og-image.jpg",
  );
  const rawBaseUrl = getConfigValue(config, "site.baseUrl", "");
  const baseUrl =
    rawBaseUrl && !/^https?:\/\//i.test(rawBaseUrl)
      ? `https://${rawBaseUrl}`
      : rawBaseUrl;

  // Handle null atau undefined
  if (!ogImage) {
    return "/images/og-image.jpg"; // Fallback ke default
  }

  if (typeof ogImage === "string") {
    // Jika sudah full URL, return langsung
    if (/^https?:\/\//i.test(ogImage)) {
      return ogImage;
    }

    // Jika ada baseUrl, gabungkan jadi URL absolut
    if (baseUrl) {
      try {
        const url = new URL(
          ogImage.startsWith("/") ? ogImage : `/${ogImage}`,
          baseUrl,
        );
        return url.toString();
      } catch {
        return `${baseUrl.replace(/\/+$/, "")}/${ogImage.replace(/^\/+/, "")}`;
      }
    }
  }

  // Jika tidak bisa dibangun absolut, kembalikan apa adanya (bisa relative path)
  return ogImage;
};

/**
 * Mendapatkan Twitter handle dari config
 */
export const getTwitterHandle = (config) => {
  const twitter = getConfigValue(config, "social.twitter", null);
  if (twitter) {
    // Extract handle from URL (e.g., https://twitter.com/username -> @username)
    const match = twitter.match(/twitter\.com\/([^/]+)/);
    if (match) {
      return `@${match[1]}`;
    }
  }
  return null;
};

/**
 * Mendapatkan social media links untuk schema.org
 */
export const getSocialLinks = (config) => {
  const social = getConfigValue(config, "social", {});
  const links = [];

  if (social.facebook) links.push(social.facebook);
  if (social.instagram) links.push(social.instagram);
  if (social.twitter) links.push(social.twitter);
  if (social.youtube) links.push(social.youtube);

  return links;
};

/**
 * Mendapatkan canonical URL dari config
 */
export const getCanonicalUrl = (config, path = "") => {
  const rawBaseUrl = (getConfigValue(config, "site.baseUrl", "") ?? "").trim();

  // Normalisasi baseUrl agar selalu mengandung skema (https) jika belum ada
  const baseUrl =
    rawBaseUrl && !/^https?:\/\//i.test(rawBaseUrl)
      ? `https://${rawBaseUrl}`
      : rawBaseUrl;

  // Jika baseUrl belum dikonfigurasi, fallback ke path saja
  if (!baseUrl) {
    if (!path) return "/";
    return path.startsWith("/") ? path : `/${path}`;
  }

  try {
    const url = new URL(path || "/", baseUrl);
    const href = url.toString();

    // Hilangkan trailing slash kecuali untuk root
    if (href.endsWith("/") && url.pathname !== "/") {
      return href.slice(0, -1);
    }

    return href;
  } catch {
    // Fallback simple concat jika URL constructor gagal
    const base = baseUrl.replace(/\/+$/, "");
    const suffix = path ? `/${path.replace(/^\/+/, "")}` : "";
    return `${base}${suffix}`;
  }
};

/**
 * Mendapatkan semua remote patterns untuk Next.js Image dari config
 */
export const getImageRemotePatterns = (config) => {
  const patterns = getConfigValue(config, "api.imageRemotePatterns", []);

  // Tambahkan default patterns jika ada
  const defaultPatterns = [
    {
      protocol: "https",
      hostname: "dev.tukuya.com",
      port: "",
      pathname: "/storage/**",
    },
  ];

  return [...defaultPatterns, ...patterns];
};

/**
 * Mendapatkan CSP (Content Security Policy) dari config
 */
export const getCSP = (config) => {
  return getConfigValue(config, "security.csp", null);
};

/**
 * Mendapatkan oauth client credentials dari config
 */
export const getOAuthCredentials = (config) => {
  return {
    clientId: getConfigValue(config, "oauth.clientId", ""),
    clientSecret: getConfigValue(config, "oauth.clientSecret", ""),
  };
};
